import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';

import {
  IncomingPurchaseListResponse,
  OutgoingPurchaseListResponse,
  PageIncomingPurchaseListResponse,
  PageOutgoingPurchaseListResponse,
  PurchaseDto,
  PurchaseStatusUpdateRequest,
} from '@app/api/marketplace/models';
import { PurchaseListsService } from '@app/api/marketplace/services';
import { PurchaseListsActions } from '@app/purchase-lists/store/purchase-lists.slice';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { PageResponse } from '@shared/interfaces/page-response.interface';

@Injectable({ providedIn: 'root' })
export class PurchaseListsEffects {
  loadIncomingPurchaseList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseListsActions.loadPurchaseListsIncoming.trigger),
      switchMap((action) =>
        this.purchaseListsService.getIncomingPurchaseLists(action.filter).pipe(
          map((response: PageIncomingPurchaseListResponse) =>
            PurchaseListsActions.loadPurchaseListsIncoming.success({
              response: response as PageResponse<IncomingPurchaseListResponse>,
            })
          ),
          catchError(() =>
            of(PurchaseListsActions.loadPurchaseListsIncoming.failure())
          )
        )
      )
    )
  );

  loadOutgoingPurchaseList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseListsActions.loadPurchaseListsOutgoing.trigger),
      switchMap((action) =>
        this.purchaseListsService.getOutgoingPurchaseLists(action.filter).pipe(
          map((response: PageOutgoingPurchaseListResponse) =>
            PurchaseListsActions.loadPurchaseListsOutgoing.success({
              response: response as PageResponse<OutgoingPurchaseListResponse>,
            })
          ),
          catchError(() =>
            of(PurchaseListsActions.loadPurchaseListsOutgoing.failure())
          )
        )
      )
    )
  );

  loadPurchaseListDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseListsActions.loadActivePurchaseList.trigger),
      switchMap((action) =>
        this.purchaseListsService.getPurchaseDetails(action.request).pipe(
          map((response: PurchaseDto) =>
            PurchaseListsActions.loadActivePurchaseList.success({ response })
          ),
          catchError(() =>
            of(PurchaseListsActions.loadActivePurchaseList.failure())
          )
        )
      )
    )
  );

  updatePurchaseStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseListsActions.updatePurchaseStatus.trigger),
      exhaustMap((action: { request: PurchaseStatusUpdateRequest }) =>
        this.purchaseListsService
          .updatePurchaseListStatus({ body: action.request })
          .pipe(
            map(() => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant('SHARED.SAVE_SUCCESS'),
              });
              window.scrollTo({ top: 0, behavior: 'smooth' });
              return PurchaseListsActions.updatePurchaseStatus.success();
            }),
            catchError((error: HttpErrorResponse) =>
              of(PurchaseListsActions.updatePurchaseStatus.failure(error.error))
            )
          )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly purchaseListsService: PurchaseListsService,
    private readonly snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}
}
